namespace Ir.Components.Workbook.Settings.TreeMapSettings {
    interface IViewModel {
        config: WorkbookConfig;
        activeTabOrWidget: KnockoutObservable<ITabOrWidget>;
        activeTabItem: KnockoutObservable<number>;
        itemConfig: TreeMapConfig;

        groupFieldParams: Ir.Components.Workbook.Settings.Items.FieldMetricSetting.IFieldMetricSettingParams;
        fieldParams: Ir.Components.Workbook.Settings.Items.FieldMetricSetting.IFieldMetricSettingParams;
        metricParams: Ir.Components.Workbook.Settings.Items.FieldMetricSetting.IFieldMetricSettingParams;
        formattingRuleParams: Ir.Components.Workbook.Settings.Items.FormattingRuleSetting.IFormattingRuleSettingParams;

        topBottomValueOptions: DevExpress.ui.dxNumberBox.Properties;
        topBottomTypeOptions: DevExpress.ui.dxSelectBox.Properties;

        colorizeGroupsOptions: DevExpress.ui.dxSwitch.Properties;
        colorizationOptions: DevExpress.ui.dxSelectBox.Properties;
        layoutOptions: DevExpress.ui.dxSelectBox.Properties;
        showMetricOptions: DevExpress.ui.dxSwitch.Properties;
        formatOptions: Ir.Components.Workbook.Settings.Items.MetricFormatSetting.IMetricFormatSettingParams;
        unitFormatOptions: Ir.Components.Workbook.Settings.Items.UnitFormatSetting.IUnitFormatSettingParams;
        decimalPlacesOptions: Ir.Components.Workbook.Settings.Items.DecimalPlacesSetting.IParams;
    }
    
    export function getViewModel(this: IViewModel, params: any) {
        let self = this;
        this.config = params.config as WorkbookConfig;
        this.activeTabOrWidget = params.activeTabOrWidget;
        this.activeTabItem = params.activeTabItem;
        this.itemConfig = this.activeTabOrWidget().itemConfig() as TreeMapConfig;
        
        let currentWidget = ko.computed(() => {
            let widget = self.activeTabOrWidget()?.drilldownManager?.getActiveWidget();
            return widget instanceof TreeMapWidget ?  widget as TreeMapWidget : null;
        })
        let currentConfig = ko.computed(() => {
            return currentWidget()?.itemConfig as TreeMapConfig;
        });

        let saveFieldOrMetric = (newTabName: string = null, isMetric: boolean = false) => {
            let widget = currentWidget();
            let config = currentConfig();
            widget.saveSettings(function () {
                if (newTabName && !isMetric) {
                    widget.tabOrWidget.drilldownManager.getActiveDrillViewItem().tabName(newTabName);
                }

                if (isMetric) {
                    widget.tabOrWidget.drilldownManager.refresh();
                }
                else {
                    config.updateDrilldownListRemovingTemplateId(widget.tabOrWidget, () => {
                        widget.tabOrWidget.drilldownManager.refresh();
                    });
                }
            }, self.activeTabOrWidget().drilldownManager.activeDrilldownId);
        }

        this.metricParams = {
            label: "Metric",
            type: MappingPopoverTypeEnum.Metrics,
            id: ko.computed({
                read: function () {
                    return currentConfig()?.metric();
                },
                write: function (value: any) {
                    currentConfig().metric(value);
                }
            }) as any,
            onSelected(newValue, oldValue) {
                let config = currentConfig();
                config.metric(newValue.metricId);
                saveFieldOrMetric(null, true);
            }
        };

        this.fieldParams = {
            label: "Field",
            type: MappingPopoverTypeEnum.NonMetrics,
            id: ko.computed({
                read: function () {
                    return currentConfig()?.field();
                },
                write: function (value: any) {
                    currentConfig().field(value);
                }
            }) as any,
            autoGrouping: ko.computed({
                read: function () {
                    return currentConfig()?.fieldAutoGrouping();
                },
                write: function (value: any) {
                    currentConfig().fieldAutoGrouping(value);
                }
            }) as any,
            onSelected(newValue, oldValue) {
                let config = currentConfig();
                config.field(newValue.fieldId);
                config.fieldAutoGrouping(newValue.autoGrouping);
                saveFieldOrMetric(newValue.displayName);
            }
        };

        this.groupFieldParams = {
            label: "Group Field",
            type: MappingPopoverTypeEnum.NonMetrics,
            id: ko.computed({
                read: function () {
                    return currentConfig()?.groupField;
                },
                write: function (value: any) {
                    currentConfig().groupField(value);
                }
            }) as any,
            autoGrouping: ko.computed({
                read: function () {
                    return currentConfig()?.groupFieldAutoGrouping();
                },
                write: function (value: any) {
                    currentConfig().groupFieldAutoGrouping(value);
                }
            }) as any,
            onSelected(newValue, oldValue) {
                let config = currentConfig();
                config.groupField(newValue.fieldId);
                config.groupFieldAutoGrouping(newValue.autoGrouping);
                saveFieldOrMetric();
            }
        };

        this.formattingRuleParams = {
            label: "Formatting Rule",
            id: this.itemConfig.formattingRule
        }

        this.topBottomValueOptions = {
            format: '0#',
            value: this.itemConfig.topBottomValue as any,
            step: 1,
            showSpinButtons: isMobile() ? false : true,
            min: 1,
            width: 125,
            disabled: ko.computed(function () { return self.itemConfig.topBottomType() === 1; }) as any,
        }
        this.topBottomTypeOptions = {
            displayExpr: 'text',
            valueExpr: 'id',
            value: this.itemConfig.topBottomType,
            items: [
                { id: TopBottomEnum.All, text: 'All' },
                { id: TopBottomEnum.Top, text: 'Top' },
                { id: TopBottomEnum.Bottom, text: 'Bottom' }
            ],
            width: 80,
        }

        this.colorizeGroupsOptions = {
            value: this.itemConfig.colorizeGroups as any
        };

        this.colorizationOptions = {
            displayExpr: 'text',
            valueExpr: 'id',
            value: this.itemConfig.colorization,
            items: [
                { id: 'discrete', text: 'Discrete' },
                { id: 'gradient', text: 'Gradient' },
                { id: 'range', text: 'Range' }
            ],
            width: 110
        };

        this.layoutOptions = {
            displayExpr: 'text',
            valueExpr: 'id',
            value: this.itemConfig.layout,
            items: [
                { id: 'squarified', text: 'Squarified' },
                { id: 'strip', text: 'Strip' },
                { id: 'sliceAndDice', text: 'Slice and Dice' }
            ],
            width: 110
        };

        this.showMetricOptions = {
            value: this.itemConfig.label.showMetric as any
        };

        this.formatOptions = {
            value: this.itemConfig.label.format
        };
        this.decimalPlacesOptions = {
            value: this.itemConfig.label.decimalPlaces
        };
        this.unitFormatOptions = {
            value: this.itemConfig.label.unitFormat
        };
    }
}